import React, { useCallback, useEffect, useMemo, useState } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Table } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowCircleDown, FaArrowCircleUp, FaCashRegister } from "react-icons/fa";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ReporteDiasCalleCobros from "./components/ReporteDiasCalleCobros";
import DashboardTesoreria from "./components/DashboardTesoreria";
import ConciliacionBancaria from "./components/ConciliacionBancaria";
import { getFirestore, collection, query, where, getDocs, doc, deleteDoc } from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import {
  obtenerMovimientosTesoreriaPorEmpresa,
  registrarMovimientoTesoreria,
} from "../../services/tesoreria/tesoreriaService";
import {
  obtenerComprobantesVentaPorEmpresa,
  registrarPagoComprobanteVenta,
  obtenerOrdenesVentaPorEmpresa,
} from "../../services/contabilidad/ventasService";
import {
  aprobarPagoProveedor,
  obtenerCuentasPagarPorEmpresa,
  registrarPagoCuentaPagar,
} from "../../services/contabilidad/comprasService";
import { obtenerAsientoPorId } from "../../services/contabilidad/asientosService";
import { checkPermission } from "../../functions/checkPermission";
import { logBusinessAction } from "../../services/AuditService";

// ==================== CONSTANTES ====================

const GRADIENTS = {
  teal: "linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)",
  cyan: "linear-gradient(135deg, #0ea5a0 0%, #2dd4bf 100%)",
  blue: "linear-gradient(135deg, #1d4ed8 0%, #60a5fa 100%)",
  amber: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
  red: "linear-gradient(135deg, #dc2626 0%, #f87171 100%)",
  green: "linear-gradient(135deg, #16a34a 0%, #4ade80 100%)",
  slate: "linear-gradient(135deg, #334155 0%, #64748b 100%)",
  emerald: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
};

const COLORS = {
  cobros: { bg: GRADIENTS.cyan, hover: "#f0fdfa", alt: "#fffbeb", text: "#0ea5a0" },
  pagos: { bg: GRADIENTS.blue, hover: "#eff6ff", alt: "#fef2f2", text: "#1d4ed8" },
  pendientesCobro: { bg: GRADIENTS.amber, hover: "#fef3c7", alt: "#fffbeb" },
  pendientesPago: { bg: GRADIENTS.red, hover: "#fee2e2", alt: "#fef2f2" },
};

const BANCOS_OPCIONES = [
  "BCP", "BBVA", "Interbank", "Scotiabank", 
  "Banco de la Nación", "BanBif", "Pichincha", "PAGO PERSONAL (SOCIO)", "Otro"
];

const VALORES_INICIALES = {
  tipo: "PAGO",
  fecha: new Date().toISOString().slice(0, 10),
  descripcion: "",
  tercero: "",
  categoria: "",
  medioPago: "TRANSFERENCIA",
  referencia: "",
  moneda: "PEN",
  monto: "",
  docTipo: "",
  docSerie: "",
  docNumero: "",
};

// ==================== UTILIDADES ====================

const formatearMonto = (value) => {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

const parseFecha = (raw) => {
  const v = String(raw || "").trim();
  if (!v) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const d = new Date(`${v}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) {
    const [dd, mm, yyyy] = v.split("/");
    const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const getFechaHoy = () => new Date().toISOString().slice(0, 10);

// ==================== COMPONENTES REUTILIZABLES ====================

/** Tarjeta de indicador con gradiente */
const IndicadorCard = ({ valor, etiqueta, gradient }) => (
  <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
    <Card.Body
      className="text-center py-4"
      style={{ background: gradient, color: "white", borderRadius: "15px" }}
    >
      <h4 className="fw-bold mb-0">{valor}</h4>
      <p className="mb-0 small opacity-75">{etiqueta}</p>
    </Card.Body>
  </Card>
);

/** Avatar circular con inicial */
const AvatarInicial = ({ nombre, gradient = GRADIENTS.slate, size = 32 }) => (
  <div
    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
    style={{
      width: `${size}px`,
      height: `${size}px`,
      background: gradient,
      fontSize: `${size * 0.25}rem`,
      fontWeight: 600,
      color: "white",
    }}
  >
    {(nombre || "?").charAt(0).toUpperCase()}
  </div>
);

/** Badge estilizado con gradiente o color */
const BadgeEstilizado = ({ children, bg, color = "white", rounded = true }) => (
  <Badge
    style={{
      background: bg,
      color,
      fontWeight: 600,
      padding: "6px 12px",
      borderRadius: rounded ? "20px" : "8px",
      fontSize: "0.75rem",
    }}
  >
    {children}
  </Badge>
);

/** Estado vacío para tablas */
const EstadoVacio = ({ icono: Icono, titulo, subtitulo, gradient }) => (
  <div className="text-center py-5">
    <div
      className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
      style={{ width: "80px", height: "80px", background: gradient }}
    >
      <Icono size={30} className="text-muted" />
    </div>
    <h6 className="text-muted">{titulo}</h6>
    <small className="text-muted">{subtitulo}</small>
  </div>
);

/** Header de tabla con gradiente */
const TablaHeader = ({ columns, gradient }) => (
  <thead style={{ background: gradient, position: "sticky", top: 0, zIndex: 3 }}>
    <tr>
      {columns.map((col, idx) => (
        <th
          key={idx}
          className={`text-white py-3 px-${col.narrow ? "2" : "3"} border-0 ${col.align || ""}`}
          style={{ fontWeight: 600, background: "transparent", width: col.width, whiteSpace: "nowrap" }}
        >
          {col.label}
        </th>
      ))}
    </tr>
  </thead>
);

/** Footer de tabla con gradiente */
const TablaFooter = ({ gradient, colSpan, totalLabel, totalValue, extraCols = 0 }) => (
  <tfoot style={{ background: gradient, position: "sticky", bottom: 0, zIndex: 3, boxShadow: "0 -2px 10px rgba(0,0,0,0.15)" }}>
    <tr>
      <td colSpan={colSpan} className="py-3 px-3 border-0" style={{ background: "transparent" }}>
        <span className="text-white fw-semibold" style={{ fontSize: "0.95rem" }}>{totalLabel}</span>
      </td>
      <td className="py-3 px-3 border-0 text-end" style={{ background: "transparent" }}>
        <span className="text-white fw-bold" style={{ fontSize: "1.15rem", textShadow: "0 1px 2px rgba(0,0,0,0.2)" }}>{totalValue}</span>
      </td>
      {extraCols > 0 && (
        <td colSpan={extraCols} className="py-3 px-3 border-0" style={{ background: "transparent" }} />
      )}
    </tr>
  </tfoot>
);

/** Encabezado de Card con título e indicador */
const CardHeaderConBadge = ({ icono: Icono, titulo, badge, gradient }) => (
  <Card.Header
    className="border-0 py-3 px-4"
    style={{ background: gradient, borderBottom: "none" }}
  >
    <div className="d-flex justify-content-between align-items-center">
      <div className="text-white">
        <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
          <Icono size={18} />
          {titulo}
        </h6>
      </div>
      <Badge
        style={{
          background: "rgba(255,255,255,0.2)",
          color: "white",
          padding: "8px 16px",
          borderRadius: "20px",
          fontWeight: 600,
        }}
      >
        {badge}
      </Badge>
    </div>
  </Card.Header>
);

// ==================== CUSTOM HOOKS & HELPERS ====================

export const esComprobanteAnuladoONc = (c, todosLosComprobantes = []) => {
  if (!c) return true;

  const tipo = String(c?.tipo || c?.tipo_comprobante || "").toUpperCase();
  const serie = String(c?.serie || "").toUpperCase();
  const numero = String(c?.numero || "");
  const correlativo = `${serie}-${numero}`.toUpperCase();

  // 1. Si el propio documento es una Nota de Crédito, no es una factura pendiente de cobro
  if (
    tipo === "NOTA_CREDITO" ||
    tipo === "07" ||
    tipo === "NC" ||
    serie.startsWith("FC") ||
    serie.startsWith("EC")
  ) {
    return true;
  }

  // 2. Si el documento tiene estado de pago o estado general anulado / cancelado por NC
  const estadoPago = String(c?.estado_pago || "").trim().toUpperCase();
  const estadoComp = String(c?.estado || "").trim().toUpperCase();

  if (
    estadoPago === "ANULADO" ||
    estadoPago === "CANCELADO_NC" ||
    estadoPago === "CANCELADO_POR_NC" ||
    estadoPago === "NC_APLICADA" ||
    estadoComp === "ANULADO" ||
    c?.anulado === true ||
    c?.tiene_nc === true ||
    Boolean(c?.nota_credito_id) ||
    Boolean(c?.nota_credito_referencia)
  ) {
    return true;
  }

  // 3. Verificar si en la lista de comprobantes existe alguna Nota de Crédito que afecte a esta factura
  if (Array.isArray(todosLosComprobantes) && todosLosComprobantes.length > 0) {
    const tieneNcAsociada = todosLosComprobantes.some((nc) => {
      const ncTipo = String(nc?.tipo || nc?.tipo_comprobante || "").toUpperCase();
      const ncSerie = String(nc?.serie || "").toUpperCase();
      const esNcDoc =
        ncTipo === "NOTA_CREDITO" ||
        ncTipo === "07" ||
        ncTipo === "NC" ||
        ncSerie.startsWith("FC") ||
        ncSerie.startsWith("EC");
      if (!esNcDoc) return false;

      const docMod = String(
        nc?.documentoModificado ||
          nc?.documento_modificado ||
          nc?.comprobante_afectado ||
          nc?.ref_correlativo ||
          ""
      ).toUpperCase();
      const refSerie = String(nc?.ref_serie || nc?.serie_modificada || "").toUpperCase();
      const refNumero = String(nc?.ref_numero || nc?.numero_modificado || "").toUpperCase();
      const refCorrelativo = refSerie && refNumero ? `${refSerie}-${refNumero}` : "";

      return (
        (docMod && (docMod === correlativo || docMod === numero)) ||
        (refCorrelativo && (refCorrelativo === correlativo || refNumero === numero))
      );
    });

    if (tieneNcAsociada) return true;
  }

  return false;
};

const useTesoreriaData = (rucEmpresa) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [movimientos, setMovimientos] = useState([]);
  const [comprobantesPendientesCobro, setComprobantesPendientesCobro] = useState([]);
  const [facturasPendientesPago, setFacturasPendientesPago] = useState([]);
  const [todosComprobantes, setTodosComprobantes] = useState([]);

  const cargarData = useCallback(async () => {
    if (!rucEmpresa) {
      setMovimientos([]);
      setComprobantesPendientesCobro([]);
      setFacturasPendientesPago([]);
      setTodosComprobantes([]);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const [movs, comprobantes, cxp] = await Promise.all([
        obtenerMovimientosTesoreriaPorEmpresa(rucEmpresa),
        obtenerComprobantesVentaPorEmpresa(rucEmpresa),
        obtenerCuentasPagarPorEmpresa(rucEmpresa),
      ]);

      setMovimientos(Array.isArray(movs) ? movs : []);

      const listComprobantes = Array.isArray(comprobantes) ? comprobantes : [];
      setTodosComprobantes(listComprobantes);

      const pendientesCobro = listComprobantes.filter((x) => {
        if (esComprobanteAnuladoONc(x, listComprobantes)) {
          return false;
        }

        const estadoPago = String(x?.estado_pago || "PENDIENTE").toUpperCase();
        const estadoDetraccion = String(x?.estado_detraccion || "").toUpperCase();
        const aplicaDetraccion = Number(x?.total || 0) > 700;
        const tieneDetraccionPendiente =
          aplicaDetraccion &&
          estadoPago === "PAGADO" &&
          (estadoDetraccion === "PENDIENTE" || (Number(x?.monto_detraccion_pendiente || 0) > 0 && !x?.numero_operacion_detraccion));

        return estadoPago !== "PAGADO" || tieneDetraccionPendiente;
      });
      setComprobantesPendientesCobro(pendientesCobro);

      const pendientesPago = (Array.isArray(cxp) ? cxp : []).filter((x) => {
        const estadoPago = String(x?.estado_pago || x?.estado || "PENDIENTE").toUpperCase();
        const tipoComp = String(x?.tipo || x?.tipo_comprobante || "").toUpperCase();
        const serie = String(x?.serie || "").toUpperCase();
        const esNc =
          tipoComp === "NOTA_CREDITO" ||
          tipoComp === "07" ||
          tipoComp === "NC" ||
          serie.startsWith("FC") ||
          serie.startsWith("EC");

        if (
          esNc ||
          estadoPago === "CANCELADO_NC" ||
          estadoPago === "CANCELADO_POR_NC" ||
          estadoPago === "ANULADO" ||
          x?.anulado === true ||
          x?.tiene_nc === true ||
          Boolean(x?.nota_credito_referencia) ||
          Boolean(x?.nota_credito_id)
        ) {
          return false;
        }
        return estadoPago !== "PAGADO" && Number(x?.saldo_pendiente || 0) > 0;
      });
      setFacturasPendientesPago(pendientesPago);
    } catch (e) {
      setError(e?.message || "No se pudo cargar información de tesorería.");
    } finally {
      setLoading(false);
    }
  }, [rucEmpresa]);

  useEffect(() => {
    cargarData();
  }, [cargarData]);

  return {
    loading,
    error,
    setError,
    movimientos,
    comprobantesPendientesCobro,
    facturasPendientesPago,
    todosComprobantes,
    recargar: cargarData,
  };
};

const useIndicadores = (movimientos, comprobantesPendientesCobro, facturasPendientesPago) => {
  const resumen = useMemo(() => {
    const pagos = movimientos
      .filter((x) => String(x?.tipo || "").toUpperCase() === "PAGO")
      .reduce((acc, x) => acc + Number(x?.monto || 0), 0);

    const cobros = movimientos
      .filter((x) => String(x?.tipo || "").toUpperCase() === "COBRO")
      .reduce((acc, x) => acc + Number(x?.monto || 0), 0);

    return { pagos, cobros, saldo: cobros - pagos };
  }, [movimientos]);

  const indicadoresPagos = useMemo(() => {
    const pendientes = facturasPendientesPago || [];
    const totalPendiente = pendientes.reduce((acc, x) => acc + Number(x?.saldo_pendiente || 0), 0);
    const pagosRegistrados = movimientos.filter((x) => String(x?.tipo || "").toUpperCase() === "PAGO").length;
    return { cantidadPendientes: pendientes.length, totalPendiente, pagosRegistrados };
  }, [facturasPendientesPago, movimientos]);

  const indicadoresCobros = useMemo(() => {
    const pendientes = comprobantesPendientesCobro || [];
    const totalPendiente = pendientes.reduce((acc, x) => acc + Number(x?.total || 0), 0);
    const cobrosRegistrados = movimientos.filter((x) => String(x?.tipo || "").toUpperCase() === "COBRO").length;
    return { cantidadPendientes: pendientes.length, totalPendiente, cobrosRegistrados };
  }, [comprobantesPendientesCobro, movimientos]);

  const indicadoresDiasCalle = useMemo(() => {
    const pendientes = (comprobantesPendientesCobro || []).filter(
      (x) => String(x?.estado_pago || "PENDIENTE").toUpperCase() !== "PAGADO"
    );
    const hoy = new Date();
    const hoyCorte = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
    const vencidas = pendientes.filter((x) => {
      const fechaBase = x?.fecha_vencimiento || x?.fecha_vcto || x?.fecha_emision || "";
      const d = parseFecha(fechaBase);
      return d && d.getTime() < hoyCorte.getTime();
    }).length;
    return {
      totalPendientes: pendientes.length,
      vencidas,
      porVencer: Math.max(0, pendientes.length - vencidas),
    };
  }, [comprobantesPendientesCobro]);

  return { resumen, indicadoresPagos, indicadoresCobros, indicadoresDiasCalle };
};
const TablaMovimientosRegistrados = ({
  movimientos,
  loading,
  seccion,
  onVerAsiento,
  loadingAsiento,
  onEliminar,
}) => {
  const gradient = seccion === "cobros" ? COLORS.cobros.bg : COLORS.pagos.bg;
  const colors = seccion === "cobros" ? COLORS.cobros : COLORS.pagos;

  const columns = [
    { label: "#", narrow: true, width: "50px", align: "text-center" },
    { label: "Fecha" },
    { label: "Tercero" },
    { label: "Descripción" },
    { label: "Categoría" },
    { label: "Medio" },
    { label: "Doc. Contable" },
    { label: "Monto", align: "text-end" },
  ];

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="mt-3 text-muted">Cargando movimientos...</p>
      </div>
    );
  }

  if (movimientos.length === 0) {
    return (
      <EstadoVacio
        icono={seccion === "cobros" ? FaArrowCircleDown : FaArrowCircleUp}
        titulo={`No hay ${seccion === "cobros" ? "cobros" : "pagos"} registrados`}
        subtitulo="Los movimientos aparecerán aquí cuando se registren"
        gradient="linear-gradient(135deg, #e2e8f0 0%, #f1f5f9 100%)"
      />
    );
  }

  const total = movimientos.reduce((acc, x) => acc + Number(x.monto || 0), 0);
  const tablaConScroll = seccion === "cobros"
    ? { maxHeight: "600px", overflowY: "auto" }
    : undefined;

  return (
    <div className="table-responsive" style={tablaConScroll}>
      <Table hover className="mb-0 align-middle" style={{ fontSize: "0.9rem" }}>
        <TablaHeader columns={columns} gradient={gradient} />
        <tbody>
          {movimientos.map((item, idx) => (
            <tr
              key={item.id}
              style={{ background: idx % 2 === 0 ? "#ffffff" : "#f8fafc", transition: "all 0.2s ease" }}
              onMouseOver={(e) => (e.currentTarget.style.background = colors.hover)}
              onMouseOut={(e) => (e.currentTarget.style.background = idx % 2 === 0 ? "#ffffff" : "#f8fafc")}
            >
              <td className="py-3 px-2 border-0 text-center">
                <span className="fw-semibold text-muted">{idx + 1}</span>
              </td>
              <td className="py-3 px-3 border-0">
                <BadgeEstilizado bg={colors.text}>{item.fecha || "-"}</BadgeEstilizado>
              </td>
              <td className="py-3 px-3 border-0">
                <span className="fw-semibold text-truncate d-block" style={{ maxWidth: "250px" }}>
                  {item.tercero || "-"}
                </span>
              </td>
              <td className="py-3 px-3 border-0">
                <span className="text-truncate d-block" style={{ maxWidth: "200px" }}>
                  {item.descripcion || "-"}
                </span>
              </td>
              <td className="py-3 px-3 border-0">
                <span
                  className="d-inline-block px-3 py-1 rounded-pill"
                  style={{ background: "#f1f5f9", fontWeight: 500, color: "#475569", fontSize: "0.8rem" }}
                >
                  {item.categoria || "-"}
                </span>
              </td>
              <td className="py-3 px-3 border-0">
                <span className="text-muted">{item.medio_pago || "-"}</span>
              </td>
              <td className="py-3 px-3 border-0">
                <div className="d-flex gap-2 align-items-center">
                  {item.estado_contable === "GENERADO" && item.asiento_id ? (
                    <Button
                      size="sm"
                      className="rounded-pill px-3"
                      style={{ background: GRADIENTS.emerald, border: "none", fontSize: "0.75rem" }}
                      onClick={() => onVerAsiento(item)}
                      disabled={loadingAsiento}
                    >
                      📄 Ver
                    </Button>
                  ) : item.estado_contable === "ERROR" ? (
                    <Badge bg="danger" className="rounded-pill px-3 py-2">⚠️ Error</Badge>
                  ) : (
                    <BadgeEstilizado bg="#f1f5f9" color="#94a3b8">⏳ Pendiente</BadgeEstilizado>
                  )}
                  <Button
                    size="sm"
                    variant="outline-danger"
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: "26px", height: "26px", padding: 0 }}
                    title="Eliminar y revertir contabilidad"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEliminar(item);
                    }}
                  >
                    🗑️
                  </Button>
                </div>
              </td>
              <td className="py-3 px-3 border-0 text-end">
                <span className="fw-bold" style={{ color: colors.text, fontSize: "1rem" }}>
                  {item.moneda || "PEN"} {formatearMonto(item.monto)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
        <TablaFooter
          gradient={gradient}
          colSpan={7}
          totalLabel={`Total: ${movimientos.length} ${seccion === "cobros" ? "cobro" : "pago"}${movimientos.length !== 1 ? "s" : ""}`}
          totalValue={`S/ ${formatearMonto(total)}`}
        />
      </Table>
    </div>
  );
};

const TablaComprobantesPendientesCobro = ({
  comprobantes,
  getDraft,
  onChange,
  onRegistrar,
  guardandoId,
  rucEmpresa,
}) => {
  const columns = [
    { label: "#", narrow: true, width: "50px", align: "text-center" },
    { label: "Comprobante" },
    { label: "Cliente" },
    { label: "Emisión" },
    { label: "Total", align: "text-end" },
    { label: "Fecha Cobro" },
    { label: "N° Operación" },
    { label: "N° Op. Detracción" },
    { label: "Estado Detracción", align: "text-center" },
    { label: "Acción", align: "text-center" },
  ];

  const listaValida = useMemo(() => {
    const list = Array.isArray(comprobantes) ? comprobantes : [];
    return list.filter((c) => !esComprobanteAnuladoONc(c, list));
  }, [comprobantes]);

  if (listaValida.length === 0) {
    return (
      <EstadoVacio
        icono={FaArrowCircleDown}
        titulo="No hay comprobantes pendientes de cobro"
        subtitulo="¡Excelente! Todas las facturas han sido cobradas"
        gradient="linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)"
      />
    );
  }

  const total = listaValida.reduce((acc, x) => {
    const estadoPago = String(x?.estado_pago || "PENDIENTE").toUpperCase();
    const aplicaDetraccion = Number(x?.total || 0) > 700;
    if (estadoPago === "PAGADO" && aplicaDetraccion) {
      return acc + Number(x?.monto_detraccion_pendiente || x?.monto_detraccion || 0);
    }
    return acc + Number(x?.total || 0);
  }, 0);

  return (
    <div className="table-responsive">
      <Table hover className="mb-0 align-middle" style={{ fontSize: "0.9rem" }}>
        <TablaHeader columns={columns} gradient={COLORS.pendientesCobro.bg} />
        <tbody>
          {listaValida.slice(0, 100).map((c, idx) => {
            const draft = getDraft(c);
            const estadoPago = String(c?.estado_pago || "PENDIENTE").toUpperCase();
            const aplicaDetraccion = Number(c?.total || 0) > 700;
            const esPendienteDetraccion = estadoPago === "PAGADO" && aplicaDetraccion;
            const estadoDetraccion = String(c?.estado_detraccion || "").toUpperCase();
            const detraccionRegistrada =
              estadoDetraccion === "REGISTRADA" ||
              Boolean(c?.numero_operacion_detraccion) ||
              Number(c?.monto_detraccion_pendiente || 0) <= 0;
            const montoPendiente = esPendienteDetraccion
              ? Number(c?.monto_detraccion_pendiente || c?.monto_detraccion || 0)
              : Number(c?.total || 0);
            return (
              <tr
                key={`cobro-${c.id}`}
                style={{ background: idx % 2 === 0 ? "#ffffff" : COLORS.pendientesCobro.alt, transition: "all 0.2s ease" }}
                onMouseOver={(e) => (e.currentTarget.style.background = COLORS.pendientesCobro.hover)}
                onMouseOut={(e) => (e.currentTarget.style.background = idx % 2 === 0 ? "#ffffff" : COLORS.pendientesCobro.alt)}
              >
                <td className="py-3 px-2 border-0 text-center">
                  <span className="fw-semibold text-muted">{idx + 1}</span>
                </td>
                <td className="py-3 px-3 border-0">
                  <BadgeEstilizado bg="#0ea5a0">
                    {c.tipo} {c.serie}-{c.numero}
                  </BadgeEstilizado>
                </td>
                <td className="py-3 px-3 border-0">
                  <span className="fw-semibold text-truncate d-block" style={{ maxWidth: "250px" }}>
                    {c.cliente_razon_social || "-"}
                  </span>
                </td>
                <td className="py-3 px-3 border-0">
                  <span className="text-muted">{c.fecha_emision || "-"}</span>
                </td>
                <td className="py-3 px-3 border-0 text-end">
                  <span className="fw-bold" style={{ color: "#0ea5a0" }}>
                    {c.moneda || "PEN"} {formatearMonto(montoPendiente)}
                  </span>
                  {esPendienteDetraccion && (
                    <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                      Detracción pendiente
                    </div>
                  )}
                </td>
                <td className="py-2 px-3 border-0" style={{ minWidth: "140px" }}>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={draft.fecha_pago}
                    onChange={(e) => onChange(c.id, "fecha_pago", e.target.value)}
                    style={{ borderRadius: "10px", border: "2px solid #e2e8f0" }}
                  />
                </td>
                <td className="py-2 px-3 border-0" style={{ minWidth: "140px" }}>
                  <Form.Control
                    size="sm"
                    value={draft.numero_operacion}
                    placeholder="OP-123456"
                    onChange={(e) => onChange(c.id, "numero_operacion", e.target.value)}
                    style={{ borderRadius: "10px", border: "2px solid #e2e8f0" }}
                  />
                </td>
                <td className="py-2 px-3 border-0" style={{ minWidth: "160px" }}>
                  <Form.Control
                    size="sm"
                    value={draft.numero_operacion_detraccion}
                    placeholder="Det-123456"
                    onChange={(e) => onChange(c.id, "numero_operacion_detraccion", e.target.value)}
                    style={{ borderRadius: "10px", border: "2px solid #e2e8f0" }}
                  />
                </td>
                <td className="py-2 px-3 border-0 text-center">
                  {!aplicaDetraccion ? (
                    <Badge bg="secondary" className="rounded-pill px-3 py-2">NO APLICA</Badge>
                  ) : !esPendienteDetraccion ? (
                    <Badge bg="secondary" className="rounded-pill px-3 py-2">N/A</Badge>
                  ) : detraccionRegistrada ? (
                    <Badge bg="success" className="rounded-pill px-3 py-2">REGISTRADA</Badge>
                  ) : (
                    <Badge bg="warning" text="dark" className="rounded-pill px-3 py-2">PENDIENTE</Badge>
                  )}
                </td>
                <td className="py-2 px-3 border-0 text-center">
                  <Button
                    size="sm"
                    className="rounded-pill px-3 fw-semibold"
                    style={{ background: GRADIENTS.emerald, border: "none" }}
                    disabled={guardandoId === c.id || !rucEmpresa}
                    onClick={() => onRegistrar(c)}
                  >
                    {guardandoId === c.id ? "⏳..." : esPendienteDetraccion ? "✓ Guardar Detracción" : "✓ Cobrar"}
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
        <TablaFooter
          gradient={COLORS.pendientesCobro.bg}
          colSpan={4}
          totalLabel={`Total: ${comprobantes.length} comprobante${comprobantes.length !== 1 ? "s" : ""}`}
          totalValue={`S/ ${formatearMonto(total)}`}
          extraCols={5}
        />
      </Table>
    </div>
  );
};

const TablaFacturasPendientesPago = ({
  facturas,
  getDraft,
  onChange,
  onRegistrar,
  guardandoId,
  rucEmpresa,
}) => {
  const columns = [
    { label: "#", narrow: true, width: "50px", align: "text-center" },
    { label: "Proveedor" },
    { label: "Emisión" },
    { label: "Vencimiento" },
    { label: "Saldo", align: "text-end" },
    { label: "Fecha Pago" },
    { label: "Banco" },
    { label: "N° Operación" },
    { label: "Acción", align: "text-center" },
  ];

  if (facturas.length === 0) {
    return (
      <EstadoVacio
        icono={FaArrowCircleUp}
        titulo="No hay facturas pendientes de pago"
        subtitulo="¡Excelente! Todas las facturas han sido pagadas"
        gradient="linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)"
      />
    );
  }

  const total = facturas.reduce((acc, x) => acc + Number(x.saldo_pendiente || 0), 0);

  return (
    <div className="table-responsive">
      <Table hover className="mb-0 align-middle" style={{ fontSize: "0.9rem" }}>
        <TablaHeader columns={columns} gradient={COLORS.pendientesPago.bg} />
        <tbody>
          {facturas.slice(0, 100).map((x, idx) => {
            const draft = getDraft(x);
            return (
              <tr
                key={`pago-${x.id}`}
                style={{ background: idx % 2 === 0 ? "#ffffff" : COLORS.pendientesPago.alt, transition: "all 0.2s ease" }}
                onMouseOver={(e) => (e.currentTarget.style.background = COLORS.pendientesPago.hover)}
                onMouseOut={(e) => (e.currentTarget.style.background = idx % 2 === 0 ? "#ffffff" : COLORS.pendientesPago.alt)}
              >
                <td className="py-3 px-2 border-0 text-center">
                  <span className="fw-semibold text-muted">{idx + 1}</span>
                </td>
                <td className="py-3 px-3 border-0">
                  <div>
                    <span className="fw-semibold d-block text-truncate" style={{ maxWidth: "250px" }}>
                      {x.proveedor_razon_social || "-"}
                    </span>
                    <small className="text-muted">{x.proveedor_ruc || "-"}</small>
                  </div>
                </td>
                <td className="py-3 px-3 border-0">
                  <span className="text-muted">{x.fecha_emision || "-"}</span>
                </td>
                <td className="py-3 px-3 border-0">
                  <span className="text-danger fw-semibold">{x.fecha_vencimiento || "-"}</span>
                </td>
                <td className="py-3 px-3 border-0 text-end">
                  <span
                    className="fw-bold px-3 py-1 rounded-pill d-inline-block"
                    style={{ background: "#fef2f2", color: "#dc2626" }}
                  >
                    {x.moneda || "PEN"} {formatearMonto(x.saldo_pendiente)}
                  </span>
                </td>
                <td className="py-2 px-3 border-0" style={{ minWidth: "130px" }}>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={draft.fecha_pago}
                    onChange={(e) => onChange(x.id, "fecha_pago", e.target.value)}
                    style={{ borderRadius: "10px", border: "2px solid #e2e8f0" }}
                  />
                </td>
                <td className="py-2 px-3 border-0" style={{ minWidth: "120px" }}>
                  <Form.Select
                    size="sm"
                    value={draft.banco}
                    onChange={(e) => onChange(x.id, "banco", e.target.value)}
                    style={{ borderRadius: "10px", border: "2px solid #e2e8f0" }}
                  >
                    {BANCOS_OPCIONES.map((banco) => (
                      <option key={banco} value={banco}>{banco}</option>
                    ))}
                  </Form.Select>
                </td>
                <td className="py-2 px-3 border-0" style={{ minWidth: "120px" }}>
                  <Form.Control
                    size="sm"
                    value={draft.numero_operacion}
                    placeholder="OP-123456"
                    onChange={(e) => onChange(x.id, "numero_operacion", e.target.value)}
                    style={{ borderRadius: "10px", border: "2px solid #e2e8f0" }}
                  />
                </td>
                <td className="py-2 px-3 border-0 text-center">
                  <Button
                    size="sm"
                    className="rounded-pill px-3 fw-semibold"
                    style={{ background: GRADIENTS.blue, border: "none" }}
                    disabled={guardandoId === x.id || !rucEmpresa}
                    onClick={() => onRegistrar(x)}
                  >
                    {guardandoId === x.id ? "⏳..." : "✓ Pagar"}
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
        <TablaFooter
          gradient={COLORS.pendientesPago.bg}
          colSpan={4}
          totalLabel={`Total: ${facturas.length} factura${facturas.length !== 1 ? "s" : ""}`}
          totalValue={`S/ ${formatearMonto(total)}`}
          extraCols={4}
        />
      </Table>
    </div>
  );
};

// ==================== MODAL DE ASIENTO ====================

const ModalAsientoDetalle = ({ visible, data, titulo, onClose, onCopiar, onExportarPdf }) => (
  <Modal show={visible} onHide={onClose} size="lg" centered>
    <Modal.Header closeButton>
      <Modal.Title>
        Detalle de asiento {data?.id ? `- ${data.id}` : ""}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {!data ? (
        <div className="text-center py-3 text-muted">Sin información del asiento.</div>
      ) : (
        <>
          <Row className="mb-3">
            <Col md={6}><strong>Movimiento:</strong> {titulo || "-"}</Col>
            <Col md={6}><strong>Fecha:</strong> {data.fecha || "-"}</Col>
            <Col md={12} className="mt-1"><strong>Glosa:</strong> {data.glosa || "-"}</Col>
          </Row>

          <h6 className="mb-2">Debe</h6>
          <Table size="sm" bordered>
            <thead>
              <tr>
                <th>Cuenta</th>
                <th>Descripción</th>
                <th className="text-end">Monto</th>
              </tr>
            </thead>
            <tbody>
              {(data.debe || []).length === 0 ? (
                <tr><td colSpan={3} className="text-center text-muted">Sin líneas</td></tr>
              ) : (
                data.debe.map((l, idx) => (
                  <tr key={`debe-${idx}`}>
                    <td>{l.cuenta || "-"}</td>
                    <td>{l.descripcion || "-"}</td>
                    <td className="text-end">{Number(l.monto || 0).toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          <h6 className="mb-2 mt-3">Haber</h6>
          <Table size="sm" bordered>
            <thead>
              <tr>
                <th>Cuenta</th>
                <th>Descripción</th>
                <th className="text-end">Monto</th>
              </tr>
            </thead>
            <tbody>
              {(data.haber || []).length === 0 ? (
                <tr><td colSpan={3} className="text-center text-muted">Sin líneas</td></tr>
              ) : (
                data.haber.map((l, idx) => (
                  <tr key={`haber-${idx}`}>
                    <td>{l.cuenta || "-"}</td>
                    <td>{l.descripcion || "-"}</td>
                    <td className="text-end">{Number(l.monto || 0).toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          <div className="d-flex justify-content-end gap-4 mt-3">
            <div><strong>Total Debe:</strong> {Number(data.total_debe || 0).toFixed(2)}</div>
            <div><strong>Total Haber:</strong> {Number(data.total_haber || 0).toFixed(2)}</div>
          </div>
        </>
      )}
    </Modal.Body>
    <Modal.Footer>
      <Button variant="outline-secondary" onClick={onCopiar} disabled={!data?.id}>
        Copiar ID
      </Button>
      <Button variant="outline-primary" onClick={onExportarPdf} disabled={!data}>
        Exportar PDF
      </Button>
      <Button variant="secondary" onClick={onClose}>
        Cerrar
      </Button>
    </Modal.Footer>
  </Modal>
);

// ==================== COMPONENTE PRINCIPAL ====================

const GestionTesoreriaModulo = ({ usuario }) => {
  const navigate = useNavigate();
  const { seccion: seccionRuta } = useParams();
  const mostrarTabsRuta = typeof seccionRuta === "string" && seccionRuta.length > 0;

  const [seccionInterna, setSeccionInterna] = useState("dashboard");
  const seccion = mostrarTabsRuta ? seccionRuta : seccionInterna;
  const permisosTesoreria = useMemo(() => {
    const accesoGlobal =
      checkPermission(usuario, "tesoreria") || checkPermission(usuario, "contabilidad", "TESORERIA");

    return {
      dashboard: accesoGlobal || checkPermission(usuario, "tesoreria", "DASHBOARD") || true,
      pagos: accesoGlobal || checkPermission(usuario, "tesoreria", "PAGOS"),
      cobros: accesoGlobal || checkPermission(usuario, "tesoreria", "COBROS"),
      "dias-calle": accesoGlobal || checkPermission(usuario, "tesoreria", "DIAS_CALLE"),
      conciliacion: accesoGlobal || checkPermission(usuario, "tesoreria", "CONCILIACION") || true,
    };
  }, [usuario]);

  const seccionesPermitidas = useMemo(
    () => Object.entries(permisosTesoreria).filter(([, ok]) => Boolean(ok)).map(([k]) => k),
    [permisosTesoreria]
  );

  const sinAccesoTesoreria = seccionesPermitidas.length === 0;
  const seccionSinPermiso = ["dashboard", "pagos", "cobros", "dias-calle", "conciliacion"].includes(seccion) && !permisosTesoreria[seccion];
  const esDashboard = seccion === "dashboard" && permisosTesoreria.dashboard;
  const esConciliacion = seccion === "conciliacion" && permisosTesoreria.conciliacion;
  const esDiasCalle = seccion === "dias-calle" && permisosTesoreria["dias-calle"];
  const esCobros = seccion === "cobros" && permisosTesoreria.cobros;
  const esPagos = seccion === "pagos" && permisosTesoreria.pagos;

  const [guardando, setGuardando] = useState(false);
  const [ok, setOk] = useState("");
  const [filtroTerceroCobros, setFiltroTerceroCobros] = useState("");
  const [filtroFechaCobros, setFiltroFechaCobros] = useState("");

  const [form, setForm] = useState(VALORES_INICIALES);
  const [cobrosPendientesDraft, setCobrosPendientesDraft] = useState({});
  const [pagosPendientesDraft, setPagosPendientesDraft] = useState({});
  const [guardandoCobroId, setGuardandoCobroId] = useState("");
  const [guardandoPagoId, setGuardandoPagoId] = useState("");
  const [planillas, setPlanillas] = useState([]);

  const [modalAsiento, setModalAsiento] = useState({ visible: false, data: null, titulo: "" });
  const [loadingAsiento, setLoadingAsiento] = useState(false);

  const rucEmpresa = (usuario?.ruc || usuario?.empresa?.ruc || "").trim();
  const usuarioRegistro = usuario?.correo || usuario?.email || usuario?.dni || "sistema";

  const [ordenesVenta, setOrdenesVenta] = useState([]);

  // Cargar planillas y ordenes de venta desde Firestore
  useEffect(() => {
    if (!rucEmpresa) {
      setPlanillas([]);
      setOrdenesVenta([]);
      return;
    }
    const db = getFirestore(firebaseApp);
    const q = query(
      collection(db, "pl_planillas"),
      where("ruc_empresa", "==", rucEmpresa)
    );
    getDocs(q).then((snapshot) => {
      const data = [];
      snapshot.forEach((doc) => {
        data.push({ id: doc.id, ...doc.data() });
      });
      setPlanillas(data);
    }).catch((err) => {
      console.error("Error al cargar planillas en tesorería:", err);
    });

    obtenerOrdenesVentaPorEmpresa(rucEmpresa).then((data) => {
      setOrdenesVenta(data || []);
    }).catch((err) => {
      console.error("Error al cargar ordenes de venta en tesorería:", err);
    });
  }, [rucEmpresa]);

  // Cargar datos
  const {
    loading,
    error,
    setError,
    movimientos,
    comprobantesPendientesCobro,
    facturasPendientesPago,
    todosComprobantes,
    recargar,
  } = useTesoreriaData(rucEmpresa);

  // Indicadores
  const { indicadoresPagos, indicadoresCobros, indicadoresDiasCalle } = useIndicadores(
    movimientos,
    comprobantesPendientesCobro,
    facturasPendientesPago
  );

  // Validar ruta
  useEffect(() => {
    if (!mostrarTabsRuta) return;
    const permitidas = ["dashboard", "pagos", "cobros", "dias-calle", "conciliacion"];
    if (!permitidas.includes(seccionRuta) || !permisosTesoreria[seccionRuta]) {
      const destino = seccionesPermitidas[0] || "dashboard";
      navigate(`/gestion-tesoreria/${destino}`, { replace: true });
    }
  }, [mostrarTabsRuta, navigate, seccionRuta, permisosTesoreria, seccionesPermitidas]);

  // Actualizar tipo de form según sección
  useEffect(() => {
    setForm((prev) => ({ ...prev, tipo: seccion === "cobros" ? "COBRO" : "PAGO" }));
  }, [seccion]);

  // Filtrar movimientos
  const movimientosFiltrados = useMemo(() => {
    const tipo = seccion === "cobros" ? "COBRO" : "PAGO";
    return movimientos.filter((x) => String(x?.tipo || "").toUpperCase() === tipo);
  }, [movimientos, seccion]);

  const movimientosMostrados = useMemo(() => {
    if (!esCobros) return movimientosFiltrados;

    const terceroBuscado = filtroTerceroCobros.trim().toLowerCase();
    const fechaBuscada = filtroFechaCobros.trim();

    return movimientosFiltrados.filter((mov) => {
      const tercero = String(mov?.tercero || "").toLowerCase();
      const fecha = String(mov?.fecha || "").trim();

      const cumpleTercero = !terceroBuscado || tercero.includes(terceroBuscado);
      const cumpleFecha = !fechaBuscada || fecha === fechaBuscada;

      return cumpleTercero && cumpleFecha;
    });
  }, [esCobros, movimientosFiltrados, filtroTerceroCobros, filtroFechaCobros]);

  const tercerosCobrosOpciones = useMemo(() => {
    if (!esCobros) return [];

    const unicos = new Set();
    movimientosFiltrados.forEach((mov) => {
      const tercero = String(mov?.tercero || "").trim();
      if (tercero) unicos.add(tercero);
    });

    return Array.from(unicos).sort((a, b) => a.localeCompare(b, "es"));
  }, [esCobros, movimientosFiltrados]);

  // Handlers de formulario
  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setOk("");

    if (!rucEmpresa) {
      setError("No se detectó la empresa activa.");
      return;
    }

    try {
      setGuardando(true);
      await registrarMovimientoTesoreria({
        rucEmpresa,
        usuarioRegistro,
        movimiento: {
          tipo: "PAGO",
          fecha: form.fecha,
          descripcion: form.descripcion,
          tercero: form.tercero,
          categoria: form.categoria,
          medioPago: form.medioPago,
          referencia: form.referencia,
          moneda: form.moneda,
          monto: Number(form.monto || 0),
          documentoRelacionado: form.docTipo
            ? { tipo: form.docTipo, serie: form.docSerie, numero: form.docNumero }
            : null,
        },
      });

      setForm({ ...VALORES_INICIALES, fecha: getFechaHoy() });
      setOk("Pago registrado correctamente.");

      // AUDITORÍA MOVIMIENTO MANUAL
      try {
        await logBusinessAction("REGISTRO_MOVIMIENTO_TESORERIA", "GestionTesoreria", {
          tipo: "PAGO",
          tercero: form.tercero,
          monto: Number(form.monto || 0),
          categoria: form.categoria
        }, usuario);
      } catch (e) {
        console.warn("No se pudo registrar la auditoría de movimiento manual", e);
      }

      await recargar();
    } catch (e2) {
      setError(e2?.message || "No se pudo registrar el pago.");
    } finally {
      setGuardando(false);
    }
  };

  // Drafts de cobros y pagos pendientes
  const getCobroPendienteDraft = (comprobante) => {
    const actual = cobrosPendientesDraft[comprobante.id] || {};
    return {
      fecha_pago: actual.fecha_pago || comprobante.fecha_pago || getFechaHoy(),
      numero_operacion: actual.numero_operacion || comprobante.numero_operacion || "",
      numero_operacion_detraccion: actual.numero_operacion_detraccion || comprobante.numero_operacion_detraccion || "",
    };
  };

  const getPagoPendienteDraft = (cxp) => {
    const actual = pagosPendientesDraft[cxp.id] || {};
    return {
      fecha_pago: actual.fecha_pago || getFechaHoy(),
      numero_operacion: actual.numero_operacion || "",
      banco: actual.banco || BANCOS_OPCIONES[0],
    };
  };

  const onCobroPendienteChange = (id, key, value) => {
    setCobrosPendientesDraft((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [key]: value },
    }));
  };

  const onPagoPendienteChange = (id, key, value) => {
    setPagosPendientesDraft((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [key]: value },
    }));
  };

  // Registrar cobro de comprobante
  const registrarCobroComprobante = async (comprobante) => {
    const draft = getCobroPendienteDraft(comprobante);
    setError("");
    setOk("");

    const estadoPago = String(comprobante?.estado_pago || "PENDIENTE").toUpperCase();
    const aplicaDetraccion = Number(comprobante?.total || 0) > 700;
    const esPendienteDetraccion = estadoPago === "PAGADO" && aplicaDetraccion;

    if (!draft.fecha_pago || !draft.numero_operacion) {
      setError("Debe registrar fecha y N° de operación para el cobro.");
      return;
    }

    if (esPendienteDetraccion && !draft.numero_operacion_detraccion) {
      setError("Debe registrar el N° de operación de detracción.");
      return;
    }

    try {
      setGuardandoCobroId(comprobante.id);
      await registrarPagoComprobanteVenta({
        rucEmpresa,
        comprobanteId: comprobante.id,
        fechaPago: draft.fecha_pago,
        numeroOperacion: draft.numero_operacion,
        numeroOperacionDetraccion: draft.numero_operacion_detraccion,
        usuarioRegistro,
      });

      if (!esPendienteDetraccion) {
        await registrarMovimientoTesoreria({
          rucEmpresa,
          usuarioRegistro,
          movimiento: {
            tipo: "COBRO",
            fecha: draft.fecha_pago,
            descripcion: `Cobro ${comprobante.tipo || "FACTURA"} ${comprobante.serie || ""}-${comprobante.numero || ""}`.trim(),
            tercero: comprobante.cliente_razon_social || comprobante.cliente_ruc || "Cliente",
            categoria: "VENTAS",
            medioPago: "TRANSFERENCIA",
            referencia: draft.numero_operacion,
            moneda: comprobante.moneda || "PEN",
            monto: Number(comprobante.total || 0),
            documentoRelacionado: {
              tipo: comprobante.tipo || "FACTURA",
              serie: comprobante.serie || "",
              numero: comprobante.numero || "",
              id: comprobante.id,
            },
          },
        });
      }

      setOk(esPendienteDetraccion ? "Operación de detracción registrada correctamente." : "Cobro registrado correctamente.");

      // AUDITORÍA COBRO
      try {
        await logBusinessAction("COBRO_REGISTRADO", "GestionTesoreria", {
          comprobante: `${comprobante.tipo || "FACTURA"} ${comprobante.serie || ""}-${comprobante.numero || ""}`,
          monto: Number(comprobante.total || 0),
          cliente: comprobante.cliente_razon_social || comprobante.cliente_ruc || "Cliente"
        }, usuario);
      } catch (e) {
        console.warn("No se pudo registrar la auditoría del cobro", e);
      }

      await recargar();
    } catch (e3) {
      setError(e3?.message || "No se pudo registrar el cobro.");
    } finally {
      setGuardandoCobroId("");
    }
  };

  // Registrar pago de factura
  const registrarPagoFacturaCompra = async (cxp) => {
    const draft = getPagoPendienteDraft(cxp);
    setError("");
    setOk("");

    if (!draft.fecha_pago || !draft.numero_operacion || !draft.banco) {
      setError("Debe registrar fecha, N° de operación y banco para el pago.");
      return;
    }

    try {
      setGuardandoPagoId(cxp.id);

      const pagoId = await registrarPagoCuentaPagar({
        rucEmpresa,
        cuentaPagarId: cxp.id,
        pago: {
          fechaPago: draft.fecha_pago,
          metodoPago: draft.banco,
          referencia: draft.numero_operacion,
          monto: Number(cxp.saldo_pendiente || 0),
          usuarioRegistro,
        },
      });

      await aprobarPagoProveedor({
        pagoId,
        usuarioAprobador: usuario?.dni || usuario?.correo || usuario?.email || "sistema",
      });

      await registrarMovimientoTesoreria({
        rucEmpresa,
        usuarioRegistro,
        movimiento: {
          tipo: "PAGO",
          fecha: draft.fecha_pago,
          descripcion: `Pago proveedor ${cxp.proveedor_razon_social || ""}`.trim(),
          tercero: cxp.proveedor_razon_social || cxp.proveedor_ruc || "Proveedor",
          categoria: "COMPRAS",
          medioPago: draft.banco,
          referencia: draft.numero_operacion,
          moneda: cxp.moneda || "PEN",
          monto: Number(cxp.saldo_pendiente || 0),
          documentoRelacionado: {
            tipo: "FACTURA",
            serie: "",
            numero: "",
            id: cxp.compra_id || cxp.id,
          },
        },
      });

      setOk("Pago aplicado y registrado correctamente.");

      // AUDITORÍA PAGO CXP
      try {
        await logBusinessAction("PAGO_REGISTRADO", "GestionTesoreria", {
          proveedor: cxp.proveedor_razon_social || cxp.proveedor_ruc || "Proveedor",
          monto: Number(cxp.saldo_pendiente || 0)
        }, usuario);
      } catch (e) {
        console.warn("No se pudo registrar la auditoría de pago de CxP", e);
      }

      await recargar();
    } catch (e4) {
      setError(e4?.message || "No se pudo registrar el pago de la factura.");
    } finally {
      setGuardandoPagoId("");
    }
  };

  // Revertir y eliminar movimiento manual de tesorería y su asiento contable
  const handleEliminarMovimiento = async (item) => {
    if (!window.confirm("¿Está seguro de revertir y eliminar este movimiento de tesorería y su asiento contable de forma permanente?")) {
      return;
    }
    
    setError("");
    setOk("");
    try {
      const db = getFirestore(firebaseApp);
      // Delete the accounting seat if it exists
      if (item.asiento_id) {
        await deleteDoc(doc(db, "cont_asientos", item.asiento_id));
      }
      // Delete the treasury movement
      await deleteDoc(doc(db, "gte_movimientos", item.id));
      
      setOk("Movimiento y su asiento contable revertidos correctamente.");
      await recargar();
    } catch (e) {
      setError("No se pudo revertir el movimiento: " + e.message);
    }
  };

  // Ver detalle de asiento
  const verDetalleAsiento = async (item) => {
    const asientoId = item?.asiento_id;
    if (!asientoId || !rucEmpresa) return;

    setLoadingAsiento(true);
    setError("");
    try {
      const asiento = await obtenerAsientoPorId({ rucEmpresa, asientoId });
      if (!asiento) {
        setError("No se encontró el asiento contable.");
        return;
      }

      setModalAsiento({
        visible: true,
        data: asiento,
        titulo: `${item?.tipo || ""} ${item?.descripcion || ""}`.trim(),
      });
    } catch (e5) {
      setError(e5?.message || "No se pudo cargar el asiento.");
    } finally {
      setLoadingAsiento(false);
    }
  };

  const copiarIdAsiento = async () => {
    const id = modalAsiento?.data?.id;
    if (!id) return;
    try {
      await navigator.clipboard.writeText(id);
      setOk("ID del asiento copiado.");
    } catch {
      setError("No se pudo copiar el ID del asiento.");
    }
  };

  const exportarAsientoPdf = () => {
    const asiento = modalAsiento?.data;
    if (!asiento) return;

    const doc = new jsPDF();
    const asientoId = asiento.id || "ASIENTO";

    doc.setFontSize(14);
    doc.text(`Asiento Contable: ${asientoId}`, 14, 15);
    doc.setFontSize(10);
    doc.text(`Fecha: ${asiento.fecha || "-"}`, 14, 22);
    doc.text(`Glosa: ${asiento.glosa || "-"}`, 14, 28);

    autoTable(doc, {
      startY: 35,
      head: [["Tipo", "Cuenta", "Descripción", "Monto"]],
      body: [
        ...(asiento.debe || []).map((x) => ["DEBE", x.cuenta || "-", x.descripcion || "-", formatearMonto(x.monto)]),
        ...(asiento.haber || []).map((x) => ["HABER", x.cuenta || "-", x.descripcion || "-", formatearMonto(x.monto)]),
      ],
      styles: { fontSize: 9 },
    });

    doc.save(`asiento_${asientoId}.pdf`);
  };

  const cerrarModalAsiento = () => setModalAsiento({ visible: false, data: null, titulo: "" });

  // ==================== RENDER ====================

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "95%", maxWidth: "100%" }}>
        <Card className="shadow-lg border-0" style={{ borderRadius: "15px", overflow: "hidden", minHeight: "90vh" }}>
          {/* Header Principal */}
          <Card.Header
            className="text-white py-4 position-relative"
            style={{ background: GRADIENTS.teal, borderBottom: "3px solid #0b5f59" }}
          >
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader usuario={usuario} />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaCashRegister className="me-2" /> Gestión de Tesorería
              </h2>
              <p className="mb-0 opacity-75">Control de pagos, cobros y cobranza por antigüedad</p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Alertas */}
            {error && <Alert variant="danger">{error}</Alert>}
            {ok && <Alert variant="success">{ok}</Alert>}
            {sinAccesoTesoreria && (
              <Alert variant="danger" className="border-0 shadow-sm p-4">
                <h5 className="fw-bold">Acceso Denegado</h5>
                No tiene permisos granulares para acceder a Gestión de Tesorería.
              </Alert>
            )}
            {seccionSinPermiso && !sinAccesoTesoreria && (
              <Alert variant="warning" className="border-0 shadow-sm p-4">
                No tiene permisos para la sección seleccionada. Se le redireccionará a una sección permitida.
              </Alert>
            )}

            {!sinAccesoTesoreria && !seccionSinPermiso && (
              <>

            {/* Info Card */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1} className="text-center">
                    <div
                      className="rounded-3 p-3 d-inline-block"
                      style={{ background: GRADIENTS.teal, border: "none", color: "white" }}
                    >
                      <FaCashRegister size={20} />
                    </div>
                  </Col>
                  <Col md={7}>
                    <h5 className="mb-1 fw-semibold">Operaciones de Tesorería</h5>
                    <p className="text-muted mb-0 small">
                      La cobranza se registra desde comprobantes pendientes y los pagos desde CxP o registro manual.
                    </p>
                  </Col>
                  <Col md={4} className="text-md-end mt-3 mt-md-0">
                    <Badge bg="light" text="dark" className="px-3 py-2 rounded-pill shadow-sm">
                      Empresa: {rucEmpresa || "No definida"}
                    </Badge>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Indicadores según sección */}
            {esPagos && (
              <Row className="g-3 mb-4">
                <Col md={4}>
                  <IndicadorCard
                    valor={indicadoresPagos.cantidadPendientes}
                    etiqueta="Facturas Pendientes"
                    gradient={GRADIENTS.teal}
                  />
                </Col>
                <Col md={4}>
                  <IndicadorCard
                    valor={`S/ ${formatearMonto(indicadoresPagos.totalPendiente)}`}
                    etiqueta="Saldo por Pagar"
                    gradient={GRADIENTS.blue}
                  />
                </Col>
                <Col md={4}>
                  <IndicadorCard
                    valor={indicadoresPagos.pagosRegistrados}
                    etiqueta="Pagos Registrados"
                    gradient={GRADIENTS.slate}
                  />
                </Col>
              </Row>
            )}

            {esCobros && (
              <Row className="g-3 mb-4">
                <Col md={4}>
                  <IndicadorCard
                    valor={indicadoresCobros.cantidadPendientes}
                    etiqueta="Comprobantes Pendientes"
                    gradient={GRADIENTS.cyan}
                  />
                </Col>
                <Col md={4}>
                  <IndicadorCard
                    valor={`S/ ${formatearMonto(indicadoresCobros.totalPendiente)}`}
                    etiqueta="Saldo por Cobrar"
                    gradient={GRADIENTS.amber}
                  />
                </Col>
                <Col md={4}>
                  <IndicadorCard
                    valor={indicadoresCobros.cobrosRegistrados}
                    etiqueta="Cobros Registrados"
                    gradient={GRADIENTS.slate}
                  />
                </Col>
              </Row>
            )}

            {esDiasCalle && (
              <Row className="g-3 mb-4">
                <Col md={4}>
                  <IndicadorCard
                    valor={indicadoresDiasCalle.totalPendientes}
                    etiqueta="Documentos en Calle"
                    gradient={GRADIENTS.teal}
                  />
                </Col>
                <Col md={4}>
                  <IndicadorCard
                    valor={indicadoresDiasCalle.vencidas}
                    etiqueta="Vencidas"
                    gradient={GRADIENTS.red}
                  />
                </Col>
                <Col md={4}>
                  <IndicadorCard
                    valor={indicadoresDiasCalle.porVencer}
                    etiqueta="Por Vencer"
                    gradient={GRADIENTS.green}
                  />
                </Col>
              </Row>
            )}

            {/* Info para cobros */}
            {esCobros && (
              <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                <Card.Body>
                  <Alert variant="info" className="mb-0">
                    El cobro se registra desde la tabla de comprobantes pendientes de cobro mostrada debajo.
                  </Alert>
                </Card.Body>
              </Card>
            )}

            {/* 1. Tabla Comprobantes Pendientes de Cobro (PRIMERO) */}
            {seccion === "cobros" && (
              <Card className="border-0 shadow-lg mb-4" style={{ borderRadius: "20px", overflow: "hidden" }}>
                <CardHeaderConBadge
                  icono={FaArrowCircleDown}
                  titulo="Comprobantes Pendientes de Cobro"
                  badge={`${comprobantesPendientesCobro.length} pendientes`}
                  gradient={COLORS.pendientesCobro.bg}
                />
                <Card.Body className="p-0">
                  <TablaComprobantesPendientesCobro
                    comprobantes={comprobantesPendientesCobro}
                    getDraft={getCobroPendienteDraft}
                    onChange={onCobroPendienteChange}
                    onRegistrar={registrarCobroComprobante}
                    guardandoId={guardandoCobroId}
                    rucEmpresa={rucEmpresa}
                  />
                </Card.Body>
              </Card>
            )}

            {/* 1. Tabla Facturas Pendientes de Pago (PRIMERO) */}
            {seccion === "pagos" && (
              <Card className="border-0 shadow-lg mb-4" style={{ borderRadius: "20px", overflow: "hidden" }}>
                <CardHeaderConBadge
                  icono={FaArrowCircleUp}
                  titulo="Facturas Pendientes de Pago"
                  badge={`${facturasPendientesPago.length} pendientes`}
                  gradient={COLORS.pendientesPago.bg}
                />
                <Card.Body className="p-0">
                  <TablaFacturasPendientesPago
                    facturas={facturasPendientesPago}
                    getDraft={getPagoPendienteDraft}
                    onChange={onPagoPendienteChange}
                    onRegistrar={registrarPagoFacturaCompra}
                    guardandoId={guardandoPagoId}
                    rucEmpresa={rucEmpresa}
                  />
                </Card.Body>
              </Card>
            )}

            {/* 2. Tabla de Movimientos Registrados (SEGUNDO) */}
            {(esCobros || esPagos) && (
              <Card className="border-0 shadow-lg" style={{ borderRadius: "20px", overflow: "hidden" }}>
                <CardHeaderConBadge
                  icono={seccion === "cobros" ? FaArrowCircleDown : FaArrowCircleUp}
                  titulo={`${seccion === "cobros" ? "Cobros" : "Pagos"} Registrados`}
                  badge={`${movimientosMostrados.length} registros`}
                  gradient={seccion === "cobros" ? COLORS.cobros.bg : COLORS.pagos.bg}
                />
                {esCobros && (
                  <Card.Body className="border-bottom" style={{ background: "#f8fafc" }}>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Label className="small text-muted fw-semibold mb-1">Filtrar por tercero</Form.Label>
                        <Form.Select
                          value={filtroTerceroCobros}
                          onChange={(e) => setFiltroTerceroCobros(e.target.value)}
                        >
                          <option value="">Todos los terceros</option>
                          {tercerosCobrosOpciones.map((tercero) => (
                            <option key={tercero} value={tercero}>{tercero}</option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col md={4}>
                        <Form.Label className="small text-muted fw-semibold mb-1">Filtrar por fecha</Form.Label>
                        <Form.Control
                          type="date"
                          value={filtroFechaCobros}
                          onChange={(e) => setFiltroFechaCobros(e.target.value)}
                        />
                      </Col>
                      <Col md={2} className="d-flex align-items-end">
                        <Button
                          variant="outline-secondary"
                          className="w-100"
                          onClick={() => {
                            setFiltroTerceroCobros("");
                            setFiltroFechaCobros("");
                          }}
                        >
                          Limpiar
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                )}
                <Card.Body className="p-0">
                  <TablaMovimientosRegistrados
                    movimientos={movimientosMostrados}
                    loading={loading}
                    seccion={seccion}
                    onVerAsiento={verDetalleAsiento}
                    loadingAsiento={loadingAsiento}
                    onEliminar={handleEliminarMovimiento}
                  />
                </Card.Body>
              </Card>
            )}

            {/* Dashboard / Resumen */}
            {esDashboard && (
              <DashboardTesoreria
                movimientos={movimientos}
                comprobantesPendientesCobro={comprobantesPendientesCobro}
                facturasPendientesPago={facturasPendientesPago}
                planillas={planillas}
                ordenesVenta={ordenesVenta}
                rucEmpresa={rucEmpresa}
              />
            )}

            {/* Conciliación Bancaria */}
            {esConciliacion && (
              <ConciliacionBancaria
                movimientos={movimientos}
                planillas={planillas}
                rucEmpresa={rucEmpresa}
                usuarioRegistro={usuarioRegistro}
                recargarMovimientos={recargar}
                onRegistrarMovimientoRapido={registrarMovimientoTesoreria}
              />
            )}

            {/* Reporte Días en la Calle */}
            {esDiasCalle && (
              <ReporteDiasCalleCobros
                comprobantesPendientesCobro={comprobantesPendientesCobro}
                movimientos={movimientos}
                todosComprobantes={todosComprobantes}
                rucEmpresa={rucEmpresa}
              />
            )}
              </>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* Modal de Asiento */}
      <ModalAsientoDetalle
        visible={modalAsiento.visible}
        data={modalAsiento.data}
        titulo={modalAsiento.titulo}
        onClose={cerrarModalAsiento}
        onCopiar={copiarIdAsiento}
        onExportarPdf={exportarAsientoPdf}
      />
    </div>
  );
};

export default GestionTesoreriaModulo;
